import { Link } from "react-router-dom";
import { ArrowUpRight, Sparkles, Target, Hammer, Globe2 } from "lucide-react";
import SeoCopyBlock from "@/components/SeoCopyBlock";
import { SEO_COPY } from "@/data/seoCopy";
import useSEO from "@/hooks/useSEO";
import { COMPANY } from "@/data/clientsData";
import { organizationSchema, breadcrumbSchema } from "@/lib/seo-schemas";

const About = () => {
  useSEO({
    title: SEO_COPY.about.metaTitle,
    description:
      SEO_COPY.about.metaDescription,
    keywords:
      "about Stagehook Global, exhibition stand company, expo booth fabricator company, custom stall designer",
    path: "/about",
    jsonLd: [
      organizationSchema(),
      breadcrumbSchema([
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ]),
    ],
  });

  return (
    <main data-testid="about-page" className="pt-[72px]">
      <section className="py-24 grain relative">
        <div className="hero-blob" style={{ top: "-200px", right: "-100px", opacity: 0.25 }} />
        <div className="shg-container relative z-10">
          <div className="max-w-4xl">
            <div className="shg-eyebrow mb-5">About us</div>
            <h1 className="font-display text-6xl md:text-8xl leading-[0.92] mb-8">
              We&apos;re <span style={{ color: "var(--red)" }}>storytellers</span><br />
              who happen to build booths.
            </h1>
            <p className="text-xl text-[var(--text-soft)] max-w-2xl leading-relaxed">
              {COMPANY.description}
            </p>
          </div>
        </div>
      </section>

      <SeoCopyBlock id="about" />

      <section className="py-20" style={{ background: "var(--bg-soft)" }}>
        <div className="shg-container">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <div>
              <div className="section-tag mb-4"><span className="dot" />Our philosophy</div>
              <h2 className="font-display text-4xl md:text-5xl leading-tight">
                A stand is not a structure.<br />
                It&apos;s your brand&apos;s <span style={{ color: "var(--red)" }}>flagship store</span> for 4 days.
              </h2>
            </div>
            <p className="text-[var(--text-soft)] text-lg leading-relaxed">
              Every project we ship starts with a single question: &ldquo;What do you want visitors to remember 6 months from now?&rdquo;
              From there, our designers, fabricators and on-site crew make sure every square meter of your stand earns that memory.
              <br /><br />
              We&apos;ve been doing this since 2015 across {COMPANY.countriesServed} countries — {COMPANY.projectsBuilt} stands and counting.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20">
        <div className="shg-container">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Sparkles, title: "Design-first", desc: "In-house creative team. Concept renders in 72 hours." },
              { icon: Hammer, title: "Own fabrication", desc: "We own our workshops — no middleman, no last-minute surprises." },
              { icon: Globe2, title: "Global crew", desc: "Vetted on-ground partners in every major expo city." },
              { icon: Target, title: "ROI-focused", desc: "Designed for footfall, dwell time and qualified leads." },
            ].map((v) => {
              const Icon = v.icon;
              return (
                <div key={v.title} className="shg-card p-7" data-testid={`value-${v.title.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon className="text-[var(--red)] mb-5" size={26} strokeWidth={1.5} />
                  <h3 className="font-display text-xl mb-2">{v.title}</h3>
                  <p className="text-sm text-[var(--text-soft)] leading-relaxed">{v.desc}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24">
        <div className="shg-container">
          <div
            className="rounded-2xl p-10 md:p-14 flex flex-col md:flex-row md:items-center justify-between gap-6"
            style={{ background: "var(--surface)", border: "1px solid var(--line-strong)" }}
          >
            <h3 className="font-display text-3xl md:text-4xl">
              Let&apos;s talk about your <span style={{ color: "var(--red)" }}>next expo</span>.
            </h3>
            <Link to="/contact" className="btn-primary self-start">
              Get in touch <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

export default About;
